import { Injectable, OnDestroy } from '@angular/core';
import { Router, NavigationStart, NavigationEnd } from '@angular/router';
import { fromEvent, merge, Subject, BehaviorSubject, Observable, timer } from 'rxjs';
import { takeUntil, throttleTime, filter, first, debounceTime, distinctUntilChanged, tap, switchMap } from 'rxjs/operators';
import { MapService } from '../map services/map.service';

export interface SessionConfig {
    /** Inactivity timeout in milliseconds before automatic logout */
    inactivityTimeout: number;
    /** Threshold in milliseconds before tab switch cleanup */
    tabSwitchThreshold: number;
    /** Whether to disable back button navigation */
    disableBackNavigation: boolean;
    /** Events to track for activity */
    trackEvents: ('mousemove' | 'keydown' | 'click' | 'scroll' | 'touchstart')[];
}

/**
 * Service responsible for managing user session state, including:
 * - Activity tracking
 * - Session timeout
 * - Browser/tab close detection
 * - Back button prevention
 */
@Injectable({
    providedIn: 'root'
})
export class SessionManagementService implements OnDestroy {
    private destroy$ = new Subject<void>();
    private cleanup$ = new Subject<void>();

    // Session state management
    private _sessionState = new BehaviorSubject<'active' | 'idle' | 'cleaning'>('active');
    public readonly sessionState$ = this._sessionState.asObservable();
    private lastActivityTime: number = Date.now();
    private timeoutRef: any = null;
    private tabHiddenTime: number = 0;

    private config: SessionConfig = {
        inactivityTimeout: 30 * 60 * 1000, // 30 minutes by default
        tabSwitchThreshold: 10 * 60 * 1000, // 5 minutes by default
        disableBackNavigation: true,
        trackEvents: ['mousemove', 'keydown', 'click', 'scroll', 'touchstart']
    };

    constructor(
        private _router: Router,
        private _mapService: MapService
    ) {
        // Initialize after Angular is ready (first navigation complete)
        this._router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            first(),
            takeUntil(this.destroy$)
        ).subscribe(() => {
            this.initializeSession();
        });
    }

    public configure(config: Partial<SessionConfig>): void {
        this.config = { ...this.config, ...config };

        // Re-initialize with new config if already initialized
        if (this._sessionState.value !== 'cleaning') {
            this.cleanup$.next();
            this.initializeSession();
        }
    }

    private initializeSession(): void {
        // Set up activity tracking
        this.setupActivityTracking();

        // Set up session timeout monitoring
        this.setupSessionTimeout();

        // Set up tab/window close detection
        this.setupBrowserCloseDetection();

        // Set up back button prevention if enabled
        if (this.config.disableBackNavigation) {
            this.disableBackButton();
        }
    }

    private setupActivityTracking(): void {
        // Map configuration events to actual event observables
        const activityEvents: Observable<Event>[] = this.config.trackEvents.map(eventName =>
            fromEvent(document, eventName)
        );

        // Merge all activity events
        const activity$ = merge(...activityEvents).pipe(
            throttleTime(1000), // Limit event processing
            takeUntil(merge(this.destroy$, this.cleanup$))
        );

        // Update last activity time when user interacts
        activity$.subscribe(() => {
            this.lastActivityTime = Date.now();

            // If user was idle, set back to active
            if (this._sessionState.value === 'idle') {
                this._sessionState.next('active');
            }

            // Reset inactivity timer
            this.resetInactivityTimer();
        });
    }

    private setupSessionTimeout(): void {
        // Create a timer that checks for inactivity periodically
        timer(60000, 60000).pipe( // Check every minute
            takeUntil(merge(this.destroy$, this.cleanup$))
        ).subscribe(() => {
            const inactiveTime = Date.now() - this.lastActivityTime;

            // If user has been inactive for the timeout period, log them out
            if (inactiveTime >= this.config.inactivityTimeout) {
                this.performCleanup('inactivity_timeout').catch(err =>
                    console.error('Failed to log out after inactivity timeout:', err)
                );
            }
            // If user is approaching timeout, mark as idle
            else if (inactiveTime >= (this.config.inactivityTimeout * 0.8)) {
                this._sessionState.next('idle');
            }
        });

        // Initialize the inactivity timer
        this.resetInactivityTimer();
    }

    private resetInactivityTimer(): void {
        if (this.timeoutRef) {
            clearTimeout(this.timeoutRef);
        }

        this.timeoutRef = setTimeout(() => {
            this.performCleanup('inactivity_timeout').catch(err =>
                console.error('Failed to log out after inactivity timeout:', err)
            );
        }, this.config.inactivityTimeout);
    }

    private setupBrowserCloseDetection(): void {
        // Handle tab/browser close with beforeunload
        fromEvent(window, 'beforeunload').pipe(
            takeUntil(merge(this.destroy$, this.cleanup$))
        ).subscribe(async (event: BeforeUnloadEvent) => {
            if (this._sessionState.value === 'cleaning') {
                // Don't allow closing while cleanup is in progress
                event.preventDefault();
                event.returnValue = "Cleanup in progress. Please wait.";
                return event.returnValue;
            }

            // Perform cleanup when window/tab is closing
            try {
                this._sessionState.next('cleaning');
                await this.performCleanup('window_close');
            } catch (error) {
                console.error('Error during window close cleanup:', error);
            }

            return undefined;
        });

        // Handle tab visibility change
        fromEvent(document, 'visibilitychange').pipe(
            takeUntil(merge(this.destroy$, this.cleanup$)),
            tap(() => {
                if (document.visibilityState === 'hidden') {
                    // Store the time when the tab was hidden
                    this.tabHiddenTime = Date.now();
                }
            }),
            filter(() => document.visibilityState === 'visible'),
            debounceTime(300), // Debounce visibility changes
        ).subscribe(async () => {
            if (document.visibilityState === 'visible') {
                // Tab is visible again
                const hiddenDuration = Date.now() - this.tabHiddenTime;

                // Only perform cleanup if the tab was hidden longer than the threshold
                if (hiddenDuration > this.config.tabSwitchThreshold) {
                    try {
                        await this.performCleanup('tab_switch_timeout');
                    } catch (error) {
                        console.error('Error during tab switch cleanup:', error);
                    }
                } else {
                    // Just update the activity time
                    this.lastActivityTime = Date.now();
                }
            }
        });
    }

    private disableBackButton(): void {
        // Add a single history entry for our starting point
        history.pushState({ page: 'locked' }, '', location.href);

        // Create a single event listener for popstate events
        const handlePopState = (event: PopStateEvent) => {
            // Check if the user is trying to navigate back
            if (!event.state || event.state.page !== 'locked') {
                // Push state again to prevent the back navigation
                history.pushState({ page: 'locked' }, '', location.href);
            }
        };

        // Add our popstate listener
        window.addEventListener('popstate', handlePopState);

        // Clean up on service destroy or reconfiguration
        merge(this.destroy$, this.cleanup$).subscribe(() => {
            window.removeEventListener('popstate', handlePopState);
        });

        // For Angular Router integration, intercept NavigationStart events
        this._router.events.pipe(
            filter(event => event instanceof NavigationStart),
            filter((event: NavigationStart) => event.navigationTrigger === 'popstate'),
            takeUntil(merge(this.destroy$, this.cleanup$))
        ).subscribe(() => {
            // If current route should be locked, prevent navigation
            // We only allow navigation during cleanup
            if (this._sessionState.value !== 'cleaning') {
                // Cancel current navigation
                this._router.navigateByUrl(this._router.url, {
                    skipLocationChange: true,
                    replaceUrl: true
                });
            }
        });
    }

    public resetActivity(): void {
        this.lastActivityTime = Date.now();
        this._sessionState.next('active');
        this.resetInactivityTimer();
    }

    async performCleanup(reason: string): Promise<void> {
        // Prevent concurrent cleanup operations
        if (this._sessionState.value === 'cleaning') {
            return;
        }

        // Update session state
        this._sessionState.next('cleaning');

        // Log the cleanup reason
        console.log(`Performing session cleanup due to: ${reason}`);


    }

    ngOnDestroy(): void {
        // Clean up timers
        if (this.timeoutRef) {
            clearTimeout(this.timeoutRef);
        }

        // Trigger cleanup subject to cancel all internal subscriptions
        this.cleanup$.next();
        this.cleanup$.complete();

        // Trigger destroy subject to cancel all service-level subscriptions
        this.destroy$.next();
        this.destroy$.complete();

        // Clean up map resources
        this._mapService.destroyMap();
    }
}